import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { partners } from '@/data/siteData';
import { ArrowRight, Handshake, Shield, Cloud, Monitor, Server, CheckCircle2 } from 'lucide-react';

// Generate initials for partner logo placeholder
const getInitials = (name) => {
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
};

// Color variations for visual variety
const getColorGradient = (name) => {
  const colors = [
    'from-[#0066CC] to-[#004499]',
    'from-[#0077CC] to-[#0055AA]',
    'from-[#0088DD] to-[#0066BB]',
    'from-[#0055AA] to-[#003377]',
  ];
  return colors[name.charCodeAt(0) % colors.length];
};

// Partner Logo Component with fallback
const PartnerLogo = ({ name, logo, size = 'md' }) => {
  const sizeClasses = {
    sm: { container: 'w-16 h-16', text: 'text-sm', img: 'h-12' },
    md: { container: 'w-20 h-20', text: 'text-base', img: 'h-14' },
    lg: { container: 'w-24 h-24', text: 'text-lg', img: 'h-16' },
  };
  
  if (logo) {
    return (
      <div className={`${sizeClasses[size].container} flex items-center justify-center`}>
        <img 
          src={logo} 
          alt={name}
          className={`${sizeClasses[size].img} w-auto max-w-full object-contain`}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`${sizeClasses[size].container} rounded-xl bg-gradient-to-br ${getColorGradient(name)} items-center justify-center shadow-md hidden`}>
          <span className={`text-white font-bold ${sizeClasses[size].text}`}>{getInitials(name)}</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses[size].container} rounded-xl bg-gradient-to-br ${getColorGradient(name)} flex items-center justify-center shadow-md`}>
      <span className={`text-white font-bold ${sizeClasses[size].text}`}>{getInitials(name)}</span>
    </div>
  );
};

// Get partner logo by name
const getPartnerLogo = (partnerName) => {
  const partner = partners.find(p => p.name === partnerName);
  return partner?.logo || '';
};

// Group partners by category
const partnerCategories = {
  'Compute & Infrastructure': ['Acer', 'Apple', 'ASUS', 'Dell', 'HP', 'Lenovo', 'Hitachi', 'Pure Storage', 'ExaGrid', 'Commvault'],
  'Network & Security': ['Cisco', 'Aruba', 'Fortinet', 'Palo Alto Networks', 'Sophos', 'SentinelOne', 'Forcepoint', 'Forescout', 'FireMon', 'Cato Networks', 'Infoblox'],
  'Identity & Access': ['BeyondTrust', 'One Identity', 'Thales', 'Securden', 'Seclore'],
  'Cloud & Software': ['Microsoft', 'Oracle', 'Adobe', 'Akamai'],
  'Threat Protection': ['Proofpoint', 'Qualys', 'LinkShadow', 'Gigamon', 'Guardicore', 'K7 Security', 'AppSentinels', 'Prophaze', 'Protectt.ai', 'Scalefusion', 'Micro Focus'],
};

const categoryIcons = {
  'Compute & Infrastructure': Monitor,
  'Network & Security': Shield,
  'Identity & Access': Shield,
  'Cloud & Software': Cloud,
  'Threat Protection': Shield,
};

export const AlliancePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Technology Alliances - Felix Infotech | 40+ Global Partners</title>
        <meta name="description" content="Felix Infotech partners with 40+ global technology leaders including Cisco, Dell, HP, Microsoft, Fortinet, Palo Alto Networks, and more to deliver best-in-class enterprise IT solutions." />
        <meta name="keywords" content="Felix Infotech partners, technology alliances, Cisco partner India, Dell partner, HP partner Chennai, Fortinet partner, Palo Alto partner, Microsoft partner, IT vendor partnerships" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/alliances" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Technology Alliances - Felix Infotech | 40+ Global Partners" />
        <meta property="og:description" content="Partnering with 40+ global technology leaders including Cisco, Dell, HP, Microsoft, Fortinet to deliver enterprise-grade solutions." />
        <meta property="og:url" content="https://felixinfotech.com/alliances" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Technology Alliances - Felix Infotech" />
        <meta name="twitter:description" content="40+ global technology partnerships for enterprise-grade IT solutions." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Strategic Technology Alliances"
          subtitle="Our Partners"
          description="Partnering with 40+ global technology leaders to deliver best-in-class solutions for our customers."
          badge="Alliances"
          animationType="cyber"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'Alliances' }]}
        />

        {/* Stats */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '40+', label: 'Technology Partners' },
                { value: '20+', label: 'Security Vendors' },
                { value: '10+', label: 'Cloud Partners' },
                { value: '22+', label: 'Years Partnership' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-3xl md:text-4xl font-bold text-[#0066CC]">{stat.value}</p>
                  <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Partnership Intro */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-xs font-semibold tracking-widest uppercase text-[#0066CC] mb-3">Why Partner With Us</p>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Delivering Excellence Through
                  <br />
                  <span className="text-[#0066CC]">Strategic Partnerships</span>
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  As an authorized partner for 40+ global technology vendors, Felix Infotech brings 
                  world-class solutions to enterprises across India and the Middle East. Our partnerships 
                  ensure access to the latest technologies, certified expertise, and priority support.
                </p>
                <div className="space-y-3">
                  {[
                    'Authorized reseller and implementation partner',
                    'Certified engineers for all partner technologies',
                    'Direct vendor escalation and support',
                    'Access to partner training and certifications',
                    'Competitive pricing through partner agreements',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-3 gap-6"
              >
                {partners.filter(p => p.logo).slice(0, 9).map((partner, index) => (
                  <div
                    key={partner.name}
                    className="rounded-xl p-6 flex flex-col items-center justify-center hover:shadow-lg transition-shadow min-h-[140px]"
                  >
                    <PartnerLogo name={partner.name} logo={partner.logo} size="lg" />
                    <span className="text-sm font-medium text-gray-700 text-center mt-3">{partner.name}</span>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Partners by Category */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Partners by Category</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our comprehensive partner ecosystem covers all aspects of enterprise IT.
              </p>
            </motion.div>

            <div className="space-y-8">
              {Object.entries(partnerCategories).map(([category, categoryPartners], catIndex) => {
                const IconComponent = categoryIcons[category] || Handshake;
                return (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: catIndex * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-[#0066CC]" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{category}</h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                      {categoryPartners.map((partnerName) => {
                        const logo = getPartnerLogo(partnerName);
                        return (
                          <div
                            key={partnerName}
                            className="rounded-xl p-6 flex flex-col items-center justify-center hover:bg-[#0066CC]/5 hover:shadow-md transition-all min-h-[140px]"
                          >
                            <PartnerLogo name={partnerName} logo={logo} size="lg" />
                            <span className="text-sm font-medium text-gray-700 text-center mt-3">{partnerName}</span>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* All Partners Grid */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">All Technology Partners</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our complete ecosystem of technology partnerships.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.02 }}
                  className="rounded-xl p-6 flex flex-col items-center justify-center hover:shadow-lg transition-all min-h-[160px]"
                >
                  <PartnerLogo name={partner.name} logo={partner.logo} size="lg" />
                  <span className="text-sm font-medium text-gray-700 text-center mt-3">{partner.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0066CC]">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Become a Partner</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Interested in partnering with Felix Infotech? Let's explore opportunities together.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#0066CC] hover:bg-gray-100 font-semibold">
                Contact Us <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default AlliancePage;
